import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {captureTokenAuthError, sweetAPI} from 'app/store/sweet-api';
import {showMessages} from 'app/store/sweet-api/utils';
import {
  REPORTS_API_URL,
  REPORTSRUN_API_URL,
  REPORTSRUN_CREATE_API_URL,
} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {
  ListResponseType,
  ReportResultSerializedType,
  ReportSerializedType,
  UserJobSerializedType,
} from 'sweet-types';

type ReportRunParams = {
  ends_at: string;
  starts_at: string;
  slug: string;
  document_url?: string;
};

export const extendedAPI = sweetAPI.injectEndpoints({
  endpoints: (build) => ({
    getReports: build.query<ListResponseType<ReportSerializedType>, void>({
      query: () => REPORTS_API_URL,
      onQueryStarted: async (args, {queryFulfilled, ...api}) => {
        queryFulfilled.catch((result) => {
          captureTokenAuthError(result.error as any, api, [
            'required-not-in-scope',
          ]);
        });
      },
    }),
    getReport: build.query<ReportSerializedType, string>({
      query: (slug: string) => `${REPORTS_API_URL}${slug}/`,
      onQueryStarted: async (args, {queryFulfilled, ...api}) => {
        queryFulfilled.catch((result) => {
          captureTokenAuthError(result.error as any, api, [
            'required-not-in-scope',
          ]);
        });
      },
    }),
    runReport: build.mutation<
      UserJobSerializedType & {status: 'completed'},
      ReportRunParams
    >({
      queryFn: (
        body,
        api,
        extraOptions,
        baseQuery
      ): Promise<
        QueryReturnValue<
          UserJobSerializedType & {status: 'completed'},
          FetchBaseQueryError,
          any
        >
      > => {
        return api
          .dispatch(
            userJobDispatcher(
              (
                baseQuery({
                  url: REPORTSRUN_CREATE_API_URL,
                  body,
                  method: 'POST',
                }) as Promise<
                  QueryReturnValue<
                    {job: UserJobSerializedType},
                    FetchBaseQueryError,
                    any
                  >
                >
              ).then((response) => {
                if (response.data) {
                  return response.data;
                } else {
                  throw response.error;
                }
              })
            )
          )
          .job.then(
            (job) => ({data: job}),
            (error) => ({error})
          );
      },
      onQueryStarted: (args, api) => {
        showMessages({
          onError: () => gt("Couldn't run the report at the moment"),
        })(args, api);
      },
    }),
    getReportRun: build.query<ReportResultSerializedType, string>({
      query: (id: string) => `${REPORTSRUN_API_URL}${id}/`,
      onQueryStarted: async (args, {queryFulfilled, ...api}) => {
        queryFulfilled.catch((result) => {
          captureTokenAuthError(result.error as any, api, [
            'required-not-in-scope',
          ]);
        });
      },
    }),
  }),
});

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useQuery: useReports,
  useLazyQuery: useLazyReports,
  useQueryState: useReportsState,
} = extendedAPI.endpoints.getReports;

export const {useQuery: useReportDetail} = extendedAPI.endpoints.getReport;

export const {useMutation: useRunReport} = extendedAPI.endpoints.runReport;

export const {useQuery: useReportRunDetail} =
  extendedAPI.endpoints.getReportRun;
